/**
 * EconCalendar, Data, and MarketData Services
 */

import ApiClient from "../apiClient";
import type {
  StockQuote,
  VixData,
  GammaExposure,
  OptionsWall,
  OptionsFlow,
  MarketContext,
  IVScoreResponse,
  BasisAdjustedGexResponse,
} from "../../types/market-data";

// Econ Calendar Service
export interface EconEventItem {
  id: string;
  name: string;
  date?: string;
  time?: string;
  country: string;
  importance: 1 | 2 | 3;
  forecast?: string;
  previous?: string;
  actual?: string;
  category?: string;
  definition?: string;
  aiTicker?: string;
}

export interface EconPrintItem {
  id: string;
  eventName: string;
  date: string;
  actual: number | null;
  forecast: number | null;
  previous: number | null;
  surprise: number | null;
  direction: "beat" | "miss" | "inline" | null;
  goodBeta: boolean;
}

interface EconCalendarApiEvent extends Partial<EconEventItem> {
  impact?: "low" | "medium" | "high";
  detail?: string | null;
}

export class EconCalendarService {
  constructor(private client: ApiClient) {}

  async getEvents(params?: {
    from?: string;
    to?: string;
  }): Promise<EconEventItem[]> {
    const query = new URLSearchParams();
    if (params?.from) query.append("from", params.from);
    if (params?.to) query.append("to", params.to);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    const res = await this.client.get<{ events: EconCalendarApiEvent[] }>(
      `/api/data/econ-calendar${suffix}`,
    );
    return (res.events ?? []).map(normalizeEconCalendarEvent);
  }

  async getPrints(eventName?: string): Promise<EconPrintItem[]> {
    const suffix = eventName ? `?event=${encodeURIComponent(eventName)}` : "";
    const res = await this.client.get<{ prints: EconPrintItem[] }>(
      `/api/data/econ-prints${suffix}`,
    );
    return res.prints ?? [];
  }
}

function normalizeEconCalendarEvent(
  event: EconCalendarApiEvent,
): EconEventItem {
  return {
    id: event.id ?? "",
    name: event.name ?? "Untitled Event",
    date: event.date,
    time: event.time,
    country: event.country?.toUpperCase() || "US",
    importance: normalizeImportance(event.importance, event.impact),
    forecast: event.forecast,
    previous: event.previous,
    actual: event.actual,
    category: event.category,
    definition: event.definition ?? event.detail ?? undefined,
    aiTicker: event.aiTicker,
  };
}

function normalizeImportance(
  importance: unknown,
  impact: EconCalendarApiEvent["impact"],
): 1 | 2 | 3 {
  if (importance === 3 || importance === 2 || importance === 1)
    return importance;
  if (impact === "high") return 3;
  if (impact === "medium") return 2;
  return 1;
}

// Data Service (Supabase-backed)
export interface TradeIdeaItem {
  id: string;
  title: string;
  ticker: string;
  direction: "long" | "short" | "neutral";
  entry?: number;
  stopLoss?: number;
  takeProfit?: number;
  potentialRisk?: number;
  potentialProfit?: number;
  riskRewardRatio?: number;
  confidence?: string;
  timeframe?: string;
  sourceAgent?: string;
  agentDescription?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PerformanceResponse {
  kpis: Array<{ label: string; value: string; meta: string }>;
  count: number;
  fetchedAt: string;
}

export interface FuturesDailyBar {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  pctChange: number;
}

export interface FuturesDailyResponse {
  contract: string;
  source: string;
  bars: FuturesDailyBar[];
}

export class DataService {
  constructor(private client: ApiClient) {}

  async getTradeIdeas(): Promise<TradeIdeaItem[]> {
    try {
      const res = await this.client.get<{ tradeIdeas: TradeIdeaItem[] }>(
        "/api/data/trade-ideas",
      );
      return res.tradeIdeas ?? [];
    } catch {
      return [];
    }
  }

  async getPerformance(): Promise<PerformanceResponse> {
    try {
      return await this.client.get<PerformanceResponse>(
        "/api/data/performance",
      );
    } catch {
      return { kpis: [], count: 0, fetchedAt: new Date().toISOString() };
    }
  }

  async getMdbBrief(): Promise<{
    items: Array<{ title: string; detail: string }>;
    briefType?: string;
  }> {
    try {
      const res = await this.client.get<{
        items: Array<{ title: string; detail: string }>;
        briefType?: string;
      }>("/api/data/brief");
      return { items: res.items ?? [], briefType: res.briefType };
    } catch {
      return { items: [] };
    }
  }

  async getSchedule(): Promise<
    Array<{
      title: string;
      detail: string;
      forecast?: string;
      actual?: string;
      previous?: string;
      date?: string;
    }>
  > {
    try {
      const res = await this.client.get<{
        items: Array<{
          title: string;
          detail: string;
          forecast?: string;
          actual?: string;
          previous?: string;
          date?: string;
        }>;
      }>("/api/data/schedule");
      return res.items ?? [];
    } catch {
      return [];
    }
  }

  async generateMdbReport(): Promise<{
    content: string;
    briefType: string;
    generatedAt: string;
  }> {
    try {
      return await this.client.post<{
        content: string;
        briefType: string;
        generatedAt: string;
      }>("/api/data/brief/generate", {});
    } catch {
      return {
        content: "",
        briefType: "MDB",
        generatedAt: new Date().toISOString(),
      };
    }
  }

  /** Update trade idea status (Approved/Rejected/Closed) */
  async updateTradeIdeaStatus(
    pageId: string,
    status: string,
  ): Promise<boolean> {
    try {
      await this.client.patch<{ success: boolean }>(
        `/api/data/trade-ideas/${pageId}/status`,
        { status },
      );
      return true;
    } catch {
      return false;
    }
  }
}

// Market Data Service
export class MarketDataService {
  constructor(private client: ApiClient) {}

  async getQuote(symbol: string): Promise<StockQuote> {
    return this.client.get<StockQuote>(
      `/api/market-data/quote/${encodeURIComponent(symbol)}`,
    );
  }

  async getVix(): Promise<VixData> {
    return this.client.get<VixData>("/api/market-data/vix");
  }

  async getGex(symbol: string): Promise<GammaExposure> {
    return this.client.get<GammaExposure>(
      `/api/market-data/gex/${encodeURIComponent(symbol)}`,
    );
  }

  async getWalls(symbol: string): Promise<OptionsWall> {
    return this.client.get<OptionsWall>(
      `/api/market-data/walls/${encodeURIComponent(symbol)}`,
    );
  }

  async getFlow(symbol: string, limit?: number): Promise<OptionsFlow> {
    const suffix = limit ? `?limit=${limit}` : "";
    return this.client.get<OptionsFlow>(
      `/api/market-data/flow/${encodeURIComponent(symbol)}${suffix}`,
    );
  }

  async getBasisAdjustedGex(
    instrument: string,
  ): Promise<BasisAdjustedGexResponse> {
    return this.client.get<BasisAdjustedGexResponse>(
      `/api/market-data/basis-gex/${encodeURIComponent(instrument)}`,
    );
  }

  async getContext(symbol: string): Promise<MarketContext> {
    return this.client.get<MarketContext>(
      `/api/market-data/context/${encodeURIComponent(symbol)}`,
    );
  }

  async getIVScore(
    instrument?: string,
    price?: number,
  ): Promise<IVScoreResponse> {
    const params = new URLSearchParams();
    if (instrument) params.append("instrument", instrument);
    if (price) params.append("price", price.toString());
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.client.get<IVScoreResponse>(
      `/api/market-data/iv-score${suffix}`,
    );
  }

  async getFuturesDailyBars(params: {
    contract: string;
    from: string;
    to?: string;
  }): Promise<FuturesDailyResponse> {
    const query = new URLSearchParams({
      contract: params.contract,
      from: params.from,
    });
    if (params.to) query.set("to", params.to);
    return this.client.get<FuturesDailyResponse>(
      `/api/market-data/futures-daily?${query.toString()}`,
    );
  }
}
